import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { Button } from '@/components/ButtonComponent';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { SheetScreen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { textVariants } from '@/components/lib/typography';
import { common } from '@/styles/styles';

import { AllLeaguesList } from '../components/AllLeaguesList';
import { AllLeaguesSkeleton } from '../components/AllLeaguesSkeleton';
import { AllLeaguesItem, AllLeaguesSection, flattenLeagueSections } from '../utils/all-leagues-utils';

export type AllLeaguesStatus = 'loading' | 'retrying' | 'error' | 'ready';

type Props = {
    sections: AllLeaguesSection[];
    onSelectLeague: (league: AllLeaguesItem) => void;
} & ({ status: AllLeaguesStatus; onRetry: () => void } | { status: 'loading' | 'ready'; onRetry?: never });

export const AllLeaguesScreenContent = ({ sections, status, onSelectLeague, onRetry }: Props) => {
    const { t } = useTranslation('common');
    const items = useMemo(() => flattenLeagueSections(sections), [sections]);

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return <AllLeaguesSkeleton />;
            case 'retrying':
                return (
                    <Box flex={1} justifyContent="center" alignItems="center">
                        <LoadingSpinner size={24} displayLogo={false} />
                    </Box>
                );
            case 'error':
                return (
                    <Box flex={1} px="s40" gap="s24" justifyContent="center" alignItems="center">
                        <Text variant="titleLarge" letterSpacing={-0.43} textAlign="center">
                            {t('something_went_wrong')}
                        </Text>
                        <Button
                            label={t('retry')}
                            shape="pill"
                            size="m"
                            style={styles.retryButton}
                            textStyle={textVariants.titleMedium}
                            onPress={onRetry}
                        />
                    </Box>
                );
            case 'ready':
                return <AllLeaguesList items={items} onSelectLeague={onSelectLeague} />;
        }
    };

    return (
        <SheetScreen style={common.flex} edges={['top']}>
            <ScreenNavBar sheet title={t('all_leagues')} closeIconMode={'close'} titleOverrideStyle={styles.title} />
            {renderContent()}
        </SheetScreen>
    );
};

const styles = StyleSheet.create({
    title: {
        color: ds.colorValue.text.primary,
    },
    retryButton: {
        paddingHorizontal: 20,
    },
});
